import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { documents, type DocumentOwnerType } from '@/shared/api/resources';
import { useAuth } from '@/shared/auth/useAuth';
import { formatDateTime } from '@/shared/lib/format';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { Card, CardHeader } from '@/shared/ui/Card';
import { DataTable } from '@/shared/ui/DataTable';
import { EmptyState } from '@/shared/ui/EmptyState';
import { statusLabel } from '@/shared/ui/statusLabels';
import { useToast } from '@/shared/ui/useToast';

import { chaveDeEscrita } from './escritaDoDono';

/** Chave do cache da lista de excluidos de um dono. */
function chaveDosExcluidos(ownerType: DocumentOwnerType, ownerId?: string) {
  return ['document-deletions', ownerType, ownerId ?? ''] as const;
}

/**
 * Lista "Documentos excluidos" de um dono, sobre o registro das exclusoes do pacote
 * do tomador (document_deletions).
 *
 * Quando a exclusao guardou o arquivo (o padrao, como no OnPoint), a linha oferece
 * baixar e restaurar. Quando nao guardou, fica so o registro de que o documento
 * existiu, e a linha diz isso.
 */
export function DeletedDocuments({
  ownerType,
  ownerId,
  title = 'Documentos excluidos',
}: {
  ownerType: DocumentOwnerType;
  ownerId?: string | undefined;
  title?: string;
}) {
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const podeRestaurar = can(chaveDeEscrita(ownerType, 'documentos.documento.delete'));

  const excluidos = useQuery({
    queryKey: chaveDosExcluidos(ownerType, ownerId),
    queryFn: ({ signal }) => documents.deletions(ownerType, ownerId, signal),
  });

  const baixar = useMutation({
    mutationFn: (deletionId: string) => documents.downloadDeletion(deletionId),
    onSuccess: (link) => {
      window.open(link.url, '_blank', 'noopener,noreferrer');
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const restaurar = useMutation({
    mutationFn: (deletionId: string) => documents.restoreDeletion(deletionId),
    onSuccess: () => {
      toast.success('Documento restaurado.');
      void queryClient.invalidateQueries({ queryKey: ['documents'] });
      void queryClient.invalidateQueries({ queryKey: ['document-deletions'] });
    },
    onError: (error: Error) => toast.error(error.message),
  });

  return (
    <Card>
      <CardHeader
        title={title}
        description="Quem excluiu e quando. Com o arquivo guardado, o documento pode ser baixado e restaurado."
      />
      {excluidos.isError ? (
        <div className="p-5">
          <Alert tone="danger" title="Nao foi possivel carregar os excluidos">
            {excluidos.error.message}
          </Alert>
        </div>
      ) : (
        <DataTable
          columns={[
            { key: 'nome', header: 'Nome', render: (d) => d.title },
            {
              // Origem do documento excluido (P-COT-3).
              key: 'origem',
              header: 'Origem',
              hideBelow: 'md',
              render: (d) =>
                d.origin ? statusLabel('origemDocumento', d.origin) : '-',
            },
            {
              key: 'insercao',
              header: 'Data de insercao',
              hideBelow: 'md',
              render: (d) => formatDateTime(d.inserted_at),
            },
            {
              key: 'exclusao',
              header: 'Data da exclusao',
              render: (d) => formatDateTime(d.deleted_at),
            },
            {
              key: 'quem',
              header: 'Excluido por',
              hideBelow: 'md',
              render: (d) => d.deleted_label || '-',
            },
            {
              key: 'acoes',
              header: 'Acoes',
              align: 'right',
              render: (d) => (
                <div className="flex flex-wrap justify-end gap-2">
                  {d.file_available ? (
                    <Button
                      size="sm"
                      variant="secondary"
                      loading={baixar.isPending && baixar.variables === d.id}
                      onClick={() => baixar.mutate(d.id)}
                    >
                      Baixar
                    </Button>
                  ) : null}
                  {d.restorable && podeRestaurar ? (
                    <Button
                      size="sm"
                      loading={restaurar.isPending && restaurar.variables === d.id}
                      onClick={() => restaurar.mutate(d.id)}
                    >
                      Restaurar
                    </Button>
                  ) : null}
                  {!d.file_retained ? (
                    <span className="text-2xs text-ink-500">So o registro</span>
                  ) : null}
                </div>
              ),
            },
          ]}
          items={excluidos.data ?? []}
          keyOf={(d) => d.id}
          loading={excluidos.isPending}
          empty={<EmptyState icon="file" title="Nenhum documento excluido" />}
        />
      )}
    </Card>
  );
}
